export function resolveLinkedAvatarModelId(
  explicitAvatarModelId?: string | null,
  avatarConfig?: Record<string, unknown> | null
): string | null {
  const normalizedExplicitId = typeof explicitAvatarModelId === 'string' ? explicitAvatarModelId.trim() : '';
  if (normalizedExplicitId) {
    return normalizedExplicitId;
  }

  if (!avatarConfig) {
    return null;
  }

  const directAvatarModelId = typeof avatarConfig.avatarModelId === 'string' ? avatarConfig.avatarModelId.trim() : '';
  if (directAvatarModelId) {
    return directAvatarModelId;
  }

  const directModelId = typeof avatarConfig.modelId === 'string' ? avatarConfig.modelId.trim() : '';
  if (directModelId) {
    return directModelId;
  }

  const appearance = avatarConfig.appearance && typeof avatarConfig.appearance === 'object'
    ? avatarConfig.appearance as Record<string, unknown>
    : null;
  const appearanceModelId = typeof appearance?.modelId === 'string' ? appearance.modelId.trim() : '';
  return appearanceModelId || null;
}

export function resolveTtsBaseUrl(explicitTtsUrl?: string | null): string {
  const normalizedExplicitUrl = typeof explicitTtsUrl === 'string' ? explicitTtsUrl.trim().replace(/\/$/, '') : '';
  if (normalizedExplicitUrl) {
    return normalizedExplicitUrl;
  }

  if (typeof window === 'undefined') {
    return '';
  }

  return window.location.origin.replace(/\/$/, '');
}

/**
 * Decide whether the visual-channel WebSocket should auto-open as soon
 * as the avatar mounts (`true`), wait for a user gesture (`false`), or
 * delegate to `useHostedLiveState`'s default heuristic (`undefined`).
 *
 * Three input shapes:
 *
 *   1. `autoOpenChannel: true` — host explicitly opted into auto-open.
 *      This is the AgentLink / Decision Memory full-screen shape:
 *      the avatar IS the page, so opening the WS immediately means the
 *      bot's first prompt + blendshapes can stream in the background.
 *      Browser-security gestures (mic, audio.play) are still gated to
 *      the user's Listen tap — only the WebSocket itself opens early.
 *
 *   2. `requestedLiveSessionKind === 'contextual-chat'` (legacy default)
 *      — gate WS open on user gesture. Right behaviour for builder
 *      embeds (Architect Contextual Chat panel, Cara, Cross-Channel
 *      Simulator) where the avatar is one of many panels and opening
 *      five WebSockets per page load would be wasteful.
 *
 *   3. everything else — return `undefined` and let `useHostedLiveState`
 *      derive from `liveModeActive + handoffToken + !liveVoiceCallEnabled`.
 *      This is what voice-call hosts (CamlinVoice runtime, runtime-host
 *      `/visual` route) need.
 *
 * Pure function so the contract is unit-testable without React. Locked
 * down Apr-30 2026 (live-mode reliability fix). The pre-fix behaviour
 * (`requestedLiveSessionKind === 'contextual-chat'` always returns
 * `false`) was the silent reason AgentLink's first Listen click sat
 * doing nothing — the WS never auto-opened, `sendReady()` no-op'd
 * because `linked === false`, and the user had to click twice.
 */
export function resolveInitialLiveConnectRequested(args: {
  autoOpenChannel: boolean | undefined;
  requestedLiveSessionKind: string | undefined;
}): boolean | undefined {
  if (args.autoOpenChannel) {
    return true;
  }
  if (args.requestedLiveSessionKind === 'contextual-chat') {
    return false;
  }
  return undefined;
}

/**
 * Decide whether `requestLiveSession()` should attempt a SIP/WebRTC
 * voice call (`true`) or stay on the websocket-only path (`false`).
 *
 * The legacy default — `withVoiceCall = !isVisualOnly` — was wrong for
 * the AgentLink-shape host (browser-only contextual chat:
 * `isVisualOnly=false`, no `liveDestination` configured): the caller
 * would default to `true`, the SIP-init branch would hit
 * "Live mode voice destination is not configured" and bail with
 * `setLiveConnectRequested(false)`, leaving the visual channel closed
 * and the user stuck on a Listen button that does nothing.
 *
 * Honour an explicit caller override when supplied; otherwise infer
 * from `isVisualOnly` AND the presence of a `liveDestination`:
 *   - Visual IVR        (isVisualOnly=true,  liveDestination empty): no voice
 *   - AgentLink / DM    (isVisualOnly=false, liveDestination empty): no voice (browser-only)
 *   - Voice call host   (isVisualOnly=false, liveDestination set):  voice via SIP
 *
 * Pure function so the contract is unit-testable without React. Locked
 * down Apr-30 2026 (live-mode reliability fix).
 */
export function resolveWithVoiceCall(args: {
  callerOverride: boolean | undefined;
  isVisualOnly: boolean;
  liveDestination: string | null | undefined;
}): boolean {
  if (typeof args.callerOverride === 'boolean') {
    return args.callerOverride;
  }
  if (args.isVisualOnly) {
    return false;
  }
  const trimmed = typeof args.liveDestination === 'string'
    ? args.liveDestination.trim()
    : '';
  return trimmed.length > 0;
}

/**
 * Decide what the live controller should do when the SIP/WebRTC voice leg
 * reports a termination-shaped session state.
 *
 * The three termination states and where they come from
 * (`voiceCallFeature.makeCall`):
 *   - 'ended'           — JSSIP session ended (remote BYE — e.g. CamlinVoice's
 *                         ~120s caller-silence hangup — or local hangup).
 *   - 'failed'          — JSSIP session failed before/at establishment.
 *   - 'connection-lost' — voiceCallFeature's own media watchdog: ICE/peer
 *                         connection 'failed', or 'disconnected' that
 *                         outlived the 3s debounce.
 *
 * Decision ladder:
 *
 *   1. `manualHangup` → 'none'. stopLiveListening already ran the full
 *      cleanup; trailing session events must not re-trigger UI changes.
 *   2. Prompt NOT started yet (`promptStarted === false`) →
 *      'queue-reconnect'. The call died during setup; the established
 *      behaviour is to redial (≤2 attempts) via queueLiveVoiceReconnect,
 *      which then lands on status 'error' if the retries exhaust.
 *   3. Prompt started (the avatar has spoken — the call was genuinely up)
 *      → 'end-session'. This was the honesty gap (Aug 29 2026): these
 *      events were previously IGNORED after the prompt started, so a
 *      remote hangup left the timer running and the mic state stale
 *      forever. There is no ICE-restart path in voiceCallFeature, so a
 *      reported 'connection-lost' does not self-heal either — ending the
 *      session is the honest move for all three states.
 *
 * Pure function so the contract is unit-testable without React.
 */
export type VoiceSessionTerminalAction = 'end-session' | 'queue-reconnect' | 'none';

export function resolveVoiceSessionTerminalAction(args: {
  state: string;
  promptStarted: boolean;
  manualHangup: boolean;
}): VoiceSessionTerminalAction {
  if (args.state !== 'ended' && args.state !== 'failed' && args.state !== 'connection-lost') {
    return 'none';
  }
  if (args.manualHangup) {
    return 'none';
  }
  return args.promptStarted ? 'end-session' : 'queue-reconnect';
}

/**
 * Decide whether the hosted live surface should run browser STT
 * (`sttFromMic` / Azure) after the avatar finishes speaking.
 *
 * Two live listen shapes exist:
 *
 *   1. Websocket-only Listen dock (`activeListen && !liveVoiceCallEnabled`)
 *      — AgentLink / Companion / the legacy LiveControlDock. There is no
 *      CamlinVoice ASR, so the browser must transcribe.
 *
 *   2. Host-managed WebRTC (`handleInputExternally && liveVoiceCallEnabled`)
 *      — Decision Memory Real Call / Voice. SIP/WebRTC is up so the
 *      avatar can speak, but the host brain consumes `avatar:transcript`
 *      (not CamlinVoice → runtime `/generate`). Phase 4 freezes
 *      `activeListen: false` to hide the old Listen dock, which used to
 *      also disable browser STT — leaving a live-looking call that never
 *      hears the user if the voice-leg ASR is silent.
 *
 * Visual IVR (`isVisualOnly`) must stay off this path: the phone owns
 * the microphone.
 *
 * Pure function so the contract is unit-testable without React.
 */
export function usesBrowserSttOnLiveSurface(args: {
  isVisualOnly: boolean;
  activeListen: boolean;
  liveVoiceCallEnabled: boolean;
  handleInputExternally?: boolean;
}): boolean {
  if (args.isVisualOnly) {
    return false;
  }
  if (args.activeListen && !args.liveVoiceCallEnabled) {
    return true;
  }
  return Boolean(args.handleInputExternally) && args.liveVoiceCallEnabled;
}

/**
 * When host-managed live WebRTC is transcribing in the browser, ignore
 * CamlinVoice `visual.transcript` finals so the host brain does not
 * answer the same utterance twice.
 */
export function shouldSkipVoiceLegTranscriptFinal(args: {
  isFinal: boolean;
  isVisualOnly: boolean;
  handleInputExternally?: boolean;
  liveVoiceCallEnabled: boolean;
  browserSttListening: boolean;
}): boolean {
  if (!args.isFinal || args.isVisualOnly || !args.browserSttListening) {
    return false;
  }
  return Boolean(args.handleInputExternally) && args.liveVoiceCallEnabled;
}

